import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card'
import { ArrowLeft, Buildings, Briefcase, Users, Calendar } from '@phosphor-icons/react'
import { getTenantWithStats, TenantWithStats } from '../../services/tenants'
import { getAllCompanies } from '../../services/companies'

export default function TenantDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { showError } = useDialog()
  const [tenant, setTenant] = useState<TenantWithStats | null>(null)
  const [companies, setCompanies] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      if (!id) return

      try {
        const [tenantData, companiesData] = await Promise.all([
          getTenantWithStats(id),
          getAllCompanies(id)
        ])
        setTenant(tenantData)
        setCompanies(companiesData)
      } catch (error) {
        console.error('Error loading tenant:', error)
        showError('Erro', 'Erro ao carregar tenant')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [id])

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando...</div>
        </div>
      </AppLayout>
    )
  }

  if (!tenant) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Tenant não encontrado</div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/master/tenants')}
                className="gap-2"
              >
                <ArrowLeft size={18} />
                Voltar
              </Button>
              <div className="h-6 w-px bg-gray-200" />
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">{tenant.name}</h1>
                <p className="text-sm text-gray-500">Tenant ID: {tenant.id.slice(0, 8)}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total de Empresas</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {Number(tenant.shippersCount || 0) + Number(tenant.freightForwardersCount || 0)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {tenant.shippersCount || 0} embarcadores • {tenant.freightForwardersCount || 0} agentes
                    </p>
                  </div>
                  <Briefcase size={32} className="text-emerald-600" weight="duotone" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Total de Usuários</p>
                    <p className="text-3xl font-bold text-gray-900">{tenant.usersCount || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Usuários ativos</p>
                  </div>
                  <Users size={32} className="text-blue-600" weight="duotone" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Criado em</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(tenant.createdAt).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {Math.floor((new Date().getTime() - new Date(tenant.createdAt).getTime()) / (1000 * 60 * 60 * 24))} dias atrás
                    </p>
                  </div>
                  <Calendar size={32} className="text-purple-600" weight="duotone" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Companies List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Empresas</CardTitle>
                  <CardDescription>
                    Lista de todas as empresas deste tenant
                  </CardDescription>
                </div>
                <Button
                  onClick={() => navigate(`/master/companies/new?tenantId=${tenant.id}`)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Nova Empresa
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {companies.length === 0 ? (
                <div className="text-center py-12">
                  <Buildings size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
                  <p className="text-gray-500 text-sm">Nenhuma empresa cadastrada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {companies.map((company) => (
                    <div
                      key={company.companyId}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                          <Buildings size={20} className="text-emerald-600" weight="duotone" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {company.tradingName || company.name}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              company.companyType === 'shipper'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {company.companyType === 'shipper' ? 'Embarcador' : 'Agente de Carga'}
                            </span>
                            {company.primaryIdentifier && (
                              <span>{company.primaryIdentifier}</span>
                            )}
                            <span>{company.countryCode}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/master/companies/${company.companyId}?type=${company.companyType}`)}
                      >
                        Ver Detalhes
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </AppLayout>
  )
}
